
import React from 'react';
import { Check, Pencil, ShoppingCart, Package, PackageMinus, MapPin } from "lucide-react";
import { CommandItem } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface CustomerItemProps {
  customer: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    region?: string;
    municipality?: string;
    stats?: {
      orders_count: number;
      delivered_products?: number;
      returned_products?: number;
    };
  };
  selectedCustomerId?: string;
  onSelect: (id: string, name: string) => void;
  canEdit?: boolean;
  onEditCustomer?: (customerId: string) => void;
}

const CustomerItem = ({ 
  customer, 
  selectedCustomerId, 
  onSelect, 
  canEdit = false,
  onEditCustomer
}: CustomerItemProps) => {
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEditCustomer) {
      onEditCustomer(customer.id);
    }
  };

  return (
    <CommandItem
      key={customer.id}
      value={customer.id}
      onSelect={() => onSelect(customer.id, customer.name)}
      className="py-3 px-4 border-b border-gray-100 cursor-pointer transition-colors hover:bg-purple-50 dark:hover:bg-purple-900/20 data-[selected=true]:bg-purple-50 dark:data-[selected=true]:bg-purple-900/20"
    >
      <div className="flex items-center gap-3 w-full">
        <div className={cn(
          "flex items-center justify-center w-5 h-5 rounded-full border",
          selectedCustomerId === customer.id 
            ? "bg-purple-600 border-purple-600 text-white" 
            : "border-gray-300"
        )}>
          {selectedCustomerId === customer.id && (
            <Check className="h-3 w-3" />
          )}
        </div>
        
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex justify-between items-start w-full">
            <span className="font-medium truncate">{customer.name}</span>
            
            {canEdit && onEditCustomer && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="reference-table-action-button h-8 w-8 p-0 ml-2 rounded-full" 
                      onClick={handleEdit}
                    >
                      <Pencil className="h-4 w-4" />
                      <span className="sr-only">Modifier</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Modifier le client</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          
          <div className="flex flex-col text-sm text-muted-foreground space-y-1">
            {customer.email && (
              <span className="truncate">{customer.email}</span>
            )}
            {customer.phone && (
              <span>{customer.phone}</span>
            )}
            {(customer.region || customer.municipality) && (
              <span className="flex items-center gap-1 text-purple-500">
                <MapPin className="h-3 w-3" />
                {customer.region && customer.municipality 
                  ? `${customer.region}, ${customer.municipality}`
                  : customer.region || customer.municipality}
              </span>
            )}
          </div>
          
          {customer.stats && (
            <div className="flex space-x-4 mt-2 text-xs text-gray-500">
              <div className="flex items-center">
                <ShoppingCart className="h-3 w-3 mr-1 text-gray-400" />
                <span>{customer.stats.orders_count} commandes</span>
              </div>
              {typeof customer.stats.delivered_products === 'number' && (
                <div className="flex items-center">
                  <Package className="h-3 w-3 mr-1 text-green-500" />
                  <span className="text-green-600">{customer.stats.delivered_products} livrés</span>
                </div>
              )}
              {typeof customer.stats.returned_products === 'number' && (
                <div className="flex items-center">
                  <PackageMinus className="h-3 w-3 mr-1 text-red-500" />
                  <span className="text-red-600">{customer.stats.returned_products} retournés</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </CommandItem>
  );
};

export default CustomerItem;
